import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const FeatureHome = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Dashboard & Points Tracker | LOCU"
        description="Track your LOCU points in real-time. View your rewards balance, transaction history, and see how much you've saved from rent payments. 2% back on every payment."
        path="/feature/home"
        keywords="LOCU dashboard, rent reward points, points tracker, loyalty rewards balance"
      />
      {/* Hero Section */}
      <div className="bg-black text-white py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Your rent.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Now rewarding.
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 max-w-2xl mx-auto mb-12">
            Track your LOCU points, view your dashboard, and watch your rewards grow every month.
          </p>
          {!isAuthenticated && (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-semibold text-lg hover:bg-gray-100 transition"
            >
              Get Started
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Points Preview */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="text-6xl font-bold text-black mb-2">2%</div>
              <div className="text-lg text-gray-600">Points Back</div>
              <div className="text-sm text-gray-400 mt-2">On every rent payment</div>
            </div>
            <div className="text-center p-8 border-l border-r border-gray-200">
              <div className="text-6xl font-bold text-black mb-2">₹1=1</div>
              <div className="text-lg text-gray-600">Point Value</div>
              <div className="text-sm text-gray-400 mt-2">Simple conversion</div>
            </div>
            <div className="text-center p-8">
              <div className="text-6xl font-bold text-black mb-2">10%</div>
              <div className="text-lg text-gray-600">Up to Discount</div>
              <div className="text-sm text-gray-400 mt-2">At partner restaurants</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            Everything in one place
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-6">
                <span className="text-white text-xl font-bold">₹</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Points Balance</h3>
              <p className="text-gray-600">Track your rewards in real-time. See what you've earned and what's waiting to be redeemed.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-6">
                <span className="text-white text-xl font-bold">→</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Transaction History</h3>
              <p className="text-gray-600">Every rent payment, every redemption, all in one clean timeline.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-6">
                <span className="text-white text-xl font-bold">%</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Restaurant Discounts</h3>
              <p className="text-gray-600">Redeem points at partner restaurants. Get up to 10% off on your dining bills.</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to see your dashboard?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Turn your biggest expense into your favorite rewards.
          </p>
          {!isAuthenticated ? (
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-semibold text-lg hover:bg-gray-800 transition"
            >
              Sign Up to Access
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-semibold text-lg hover:bg-gray-800 transition"
            >
              Go to Dashboard
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeatureHome;
